/**
 * What is actually in the file, row by row, in construction language.
 *
 * Deliberately NOT a JSON dump and deliberately NOT a spreadsheet grid: the
 * operator is checking whether OMNΛS understood their file, and the useful
 * question is "did it read this row the way I meant it" rather than "what are
 * all 47 fields". So each row shows its identity, the six facts that matter
 * for its kind, and — where a row is broken — the reason, in the backend's own
 * words.
 *
 * On a phone this becomes a stack of cards rather than a table, because the
 * one thing a field user cannot do is scroll a wide grid sideways.
 */
import { Badge } from "../../components/ui/badge";
import { AlertTriangle, Link2, Link2Off } from "lucide-react";
import { presentPreviewRow } from "./importPresentation";

const UNKNOWN = <span className="text-muted-foreground/60">—</span>;

const DEPENDENCY_TYPE_LABEL = {
  FS: "finish → start",
  SS: "start → start",
  FF: "finish → finish",
  SF: "start → finish",
};

const PreviewRow = ({ row, kind }) => {
  const presented = presentPreviewRow(row, kind);

  return (
    <div
      className={`rounded-lg border p-2.5 space-y-1.5 min-w-0 ${
        presented.hasIssues ? "border-red-500/40 bg-red-500/5" : "border-border/60 bg-muted/20"
      }`}
      data-testid="import-preview-row"
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-medium break-all">
            {presented.title || <span className="text-muted-foreground">Untitled row</span>}
          </p>
          <p className="text-[10px] text-muted-foreground">
            {/* The line an operator opens the spreadsheet to, not the index. */}
            {/* "About": both readers skip blank rows, so a blank line above
                this one shifts the real spreadsheet line. */}
            About spreadsheet line {presented.spreadsheetLine ?? "?"}
          </p>
        </div>
        {presented.provenance && (
          <Badge
            variant="outline"
            className="bg-muted text-muted-foreground border-border text-[10px] shrink-0"
            title={presented.provenance.meaning}
          >
            {presented.provenance.label}
          </Badge>
        )}
      </div>

      <div className="flex flex-wrap gap-x-4 gap-y-1 min-w-0">
        {presented.cells.map((cell) => (
          <div key={cell.label} className="min-w-0 max-w-full">
            <p className="text-[9px] uppercase tracking-wide text-muted-foreground">{cell.label}</p>
            <p className="text-[11px] break-all">{cell.value ?? UNKNOWN}</p>
          </div>
        ))}
      </div>

      {/*
        A dependency belongs to this row — the successor is implicitly this
        activity — so it is never drawn as a free-standing edge.

        No id is shown. The preview's `predecessorId` is regenerated on every
        call and will not equal the id the commit writes, so printing it would
        put a meaningless identifier in front of an operator. What resolved,
        and what kind of link it is, is what survives.
      */}
      {presented.dependencies?.length > 0 && (
        <div className="pt-1 border-t border-border/40 space-y-0.5">
          <p className="text-[10px] text-muted-foreground flex items-center gap-1">
            <Link2 className="w-3 h-3 shrink-0" />
            {presented.dependencyCount === 1
              ? "Waits on 1 activity"
              : `Waits on ${presented.dependencyCount} activities`}
          </p>
          {presented.dependencies.map((dep, index) => (
            <p key={index} className="text-[10px] text-muted-foreground pl-4">
              {DEPENDENCY_TYPE_LABEL[dep.dependencyType] || dep.dependencyType || "link"}
              {typeof dep.lagDays === "number" && dep.lagDays !== 0 && (
                <span>
                  {" · "}
                  {dep.lagDays > 0 ? `${dep.lagDays} day lag` : `${Math.abs(dep.lagDays)} day lead`}
                </span>
              )}
            </p>
          ))}
        </div>
      )}

      {/* A named vendor that did not resolve is the single most common
          procurement failure, and the fix is never "create it here". */}
      {kind === "procurement" && presented.vendorNamed && !presented.vendorResolved && (
        <p className="text-[10px] text-amber-400 flex items-center gap-1">
          <Link2Off className="w-3 h-3 shrink-0" />
          This vendor is not in your company yet. Add them first — an import never creates one.
        </p>
      )}

      {presented.issues.length > 0 && (
        <div className="pt-1 border-t border-red-500/20 space-y-0.5">
          {presented.issues.map((issue, index) => (
            <p key={index} className="text-[11px] text-red-400 flex items-start gap-1">
              <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
              <span>{issue}</span>
            </p>
          ))}
        </div>
      )}
    </div>
  );
};

const ImportPreviewTable = ({ rows = [], kind, onlyIssues = false, truncated = false, totalRows }) => {
  const shown = onlyIssues ? rows.filter((row) => (row?.issues || []).length > 0) : rows;
  if (shown.length === 0) return null;

  return (
    <div className="space-y-1.5">
      <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
        {shown.map((row, index) => (
          <PreviewRow key={row?.rowNumber ?? index} row={row} kind={kind} />
        ))}
      </div>
      {/*
        Row detail is capped at 200 by the backend while the counts cover the
        whole file, and no truncation flag is sent — so a list that is short
        of its own count says so rather than reading as the complete picture.
      */}
      {truncated && (
        <p className="text-[10px] text-muted-foreground">
          Showing the first {shown.length.toLocaleString()}
          {typeof totalRows === "number" ? ` of ${totalRows.toLocaleString()}` : ""} rows. The counts
          above cover the whole file.
        </p>
      )}
    </div>
  );
};

export default ImportPreviewTable;
